import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store";
import { Pages, Routes } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { isAdmin } from "@/lib/auth-utils";
import { getErrorMessage } from "@/lib/error-handler";
import { Spinner } from "@/components/ui/spinner";

/**
 * SSO handoff landing. Reached from the CMS admin login after a verified OTP for a
 * super admin: exchanges the one-time `?code` for a dashboard session and drops the
 * user straight into the admin panel — no second login.
 */
export default function Sso() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code")?.trim() ?? "";
  const ssoExchange = useAuthStore((s) => s.ssoExchange);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against React StrictMode double-invoke
    ran.current = true;

    if (!code) {
      setError("رابط تسجيل الدخول غير صالح أو منتهي الصلاحية.");
      return;
    }

    (async () => {
      try {
        const response = await ssoExchange(code);
        if (response.data.user && !isAdmin(response.data.user)) {
          setError("غير مصرح لك بالوصول إلى لوحة الإدارة.");
          return;
        }
        navigate(appPath(), { replace: true });
      } catch (e) {
        setError(getErrorMessage(e));
      }
    })();
  }, [code, ssoExchange, navigate]);

  return (
    <div className="flex flex-col items-center gap-4 bg-white p-6 rounded-lg shadow w-full max-w-md text-center">
      {error ? (
        <>
          <p className="text-destructive text-sm">{error}</p>
          <Link
            to={`/${Routes.AUTH}/${Pages.LOGIN}`}
            className="text-sm underline text-foreground"
          >
            العودة إلى تسجيل الدخول
          </Link>
        </>
      ) : (
        <>
          <Spinner className="size-6 text-foreground" />
          <p className="text-sm text-muted-foreground">جارٍ تسجيل دخولك...</p>
        </>
      )}
    </div>
  );
}
